/**
 * /files/$path — explicit URL for a selected file or directory.
 *
 * The splat parameter encodes the absolute path (sans leading slash)
 * via the `encodePath` helper in `files-sidebar.tsx`. We decode it back
 * and prime the file-explorer store so the viewer mounts the right
 * module.
 *
 * The route renders nothing of its own; the parent `/files` index is
 * what paints the viewer using the store. We just keep the store in
 * sync with the URL.
 */
import { createFileRoute, useParams } from '@tanstack/react-router';
import * as React from 'react';

import { FileViewer } from '../../../components/files/file-viewer';
import { decodePath } from '../../../components/files/files-sidebar';
import { useFileExplorerStore } from '../../../stores/file-explorer-store';

function FilesPathView() {
  const params = useParams({ strict: false }) as { _splat?: string };
  const selectPath = useFileExplorerStore((s) => s.selectPath);

  const decoded = React.useMemo(() => {
    if (!params._splat) return null;
    try {
      return decodePath(params._splat);
    } catch {
      return null;
    }
  }, [params._splat]);

  React.useEffect(() => {
    if (decoded) selectPath(decoded);
  }, [decoded, selectPath]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <FileViewer path={decoded} />
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/files/$')({
  component: FilesPathView,
});
